import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, UserPlus, Trash2, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { SYSTEM_ROLES, initials } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const ROLE_COLOR = {
  Admin: "#F59E0B", "Project Manager": "#2563EB", Architect: "#8B5CF6",
  Engineer: "#10B981", "Site Worker": "#64748B", Client: "#EC4899",
};

export default function MembersTab({ projectId }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("Site Worker");

  const { data: members, isLoading } = useQuery({
    queryKey: ["members", projectId],
    queryFn: async () => (await api.get(`/projects/${projectId}/members`)).data.members,
  });

  const add = useMutation({
    mutationFn: async () => (await api.post(`/projects/${projectId}/members`, { email, role_tier: role })).data,
    onSuccess: () => {
      toast.success("Member added");
      qc.invalidateQueries({ queryKey: ["members", projectId] });
      setEmail("");
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const remove = useMutation({
    mutationFn: async (uid) => (await api.delete(`/projects/${projectId}/members/${uid}`)).data,
    onSuccess: () => {
      toast.success("Member removed");
      qc.invalidateQueries({ queryKey: ["members", projectId] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <div className="max-w-4xl">
      <div className="border border-border rounded-md bg-card p-4 mb-6">
        <Label className="label-caps flex items-center gap-1.5 mb-3"><UserPlus className="h-3.5 w-3.5" /> Add Member by Email</Label>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@company.com"
            data-testid="member-email-input" className="h-12 bg-background flex-1" />
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="h-12 bg-background sm:w-48" data-testid="member-role-select"><SelectValue /></SelectTrigger>
            <SelectContent>{SYSTEM_ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={() => add.mutate()} disabled={!email || add.isPending} data-testid="add-member-button"
            className="h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-bold">
            {add.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : "Add"}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      ) : (
        <div className="border border-border rounded-md overflow-hidden">
          {(members || []).map((m) => (
            <div key={m.id} className="flex items-center gap-4 px-4 py-3 border-b border-border last:border-0 bg-card" data-testid={`member-row-${m.id}`}>
              <div className="h-11 w-11 rounded-md bg-secondary grid place-items-center font-data font-bold shrink-0">
                {initials(m.first_name, m.last_name)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{m.first_name} {m.last_name}</div>
                <div className="text-sm text-muted-foreground truncate">{m.email}</div>
              </div>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-bold uppercase tracking-wide shrink-0"
                style={{ color: ROLE_COLOR[m.role_tier], backgroundColor: `${ROLE_COLOR[m.role_tier]}22` }}>
                <ShieldCheck className="h-3 w-3" /> {m.role_tier}
              </span>
              {m.id !== user?.id && (
                <Button variant="ghost" size="icon" className="h-10 w-10 text-muted-foreground hover:text-destructive shrink-0"
                  onClick={() => remove.mutate(m.id)} data-testid={`remove-member-${m.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
